import React from 'react';
import { Play, BookOpen } from '@phosphor-icons/react';

// Same analytics row shape from learn_my_active_courses metric
interface ActiveCourseRow {
    id: string | number;
    courseTitle: string;
    progressStatus: string;
    progressPercent: number;
    lastAccessed?: string;
    categoryName?: string;
}

interface ContinueLearningWidgetProps {
    data?: ActiveCourseRow[] | null;
    loading?: boolean;
    error?: string;
}

const getTimeAgo = (dateStr?: string): string => {
    if (!dateStr) return '';
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hours ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
};

const getAccentColor = (index: number) => {
    const colors = [
        { bg: 'bg-primary/10', icon: 'text-primary', progress: 'text-primary' },
        { bg: 'bg-amber-100', icon: 'text-amber-600', progress: 'text-amber-600' },
        { bg: 'bg-emerald-100', icon: 'text-emerald-600', progress: 'text-emerald-600' },
    ];
    return colors[index % colors.length];
};

export const ContinueLearningWidget: React.FC<ContinueLearningWidgetProps> = ({ data, loading, error }) => {
    const courses = Array.isArray(data) ? data : [];

    // Show in-progress courses sorted by most recently accessed, limit to 3
    const recentCourses = courses
        .filter(c => c.progressStatus !== 'completed' && Number(c.progressPercent) > 0)
        .sort((a, b) => {
            if (!a.lastAccessed) return 1;
            if (!b.lastAccessed) return -1;
            return new Date(b.lastAccessed).getTime() - new Date(a.lastAccessed).getTime();
        })
        .slice(0, 3);

    return (
        <div className="flex flex-col h-full">
            <div className="px-4 pt-4 pb-4 flex items-center justify-between">
                <span className="text-xs text-primary cursor-pointer hover:underline">View All</span>
            </div>
            <div className="flex-1 overflow-auto px-4 pb-4">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : recentCourses.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <BookOpen className="w-8 h-8" />
                        <span className="text-sm">No courses in progress</span>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {recentCourses.map((course, idx) => {
                            const accent = getAccentColor(idx);
                            const isFirst = idx === 0;
                            const progress = Number(course.progressPercent) || 0;
                            return (
                                <div
                                    key={course.id}
                                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition ${
                                        isFirst ? 'bg-primary/5 border border-primary/20 hover:bg-primary/10' : 'hover:bg-gray-50'
                                    }`}
                                >
                                    <div className={`w-12 h-12 rounded-lg ${accent.bg} flex items-center justify-center shrink-0`}>
                                        <Play className={`w-6 h-6 ${accent.icon}`} weight="fill" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-gray-900 truncate">{course.courseTitle}</div>
                                        {course.categoryName && (
                                            <div className="text-[11px] text-gray-400 mt-0.5 truncate">{course.categoryName}</div>
                                        )}
                                        <div className={`text-[11px] mt-1 ${isFirst ? 'text-primary' : 'text-gray-400'}`}>
                                            {course.lastAccessed ? `Last accessed ${getTimeAgo(course.lastAccessed)}` : 'Not started yet'}
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <span className={`text-xs font-semibold ${accent.progress}`}>{progress}%</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};
